import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subject, takeUntil, forkJoin, of } from 'rxjs';
import { take, filter, map, switchMap, catchError } from 'rxjs/operators';
import { Request } from 'npx-family-happy-common';
import { RequestService } from '../requests-handling/request-service/request.service';
import { AuthService } from '../authentication/auth/auth.service';
import { UserStorageService } from '../authentication/user-storage/user-storage.service';
import { ServiceAlertsService, ServiceRecordSearchResult } from './service-alerts/service-alerts.service';
import { ServiceAlertEvent } from './service-alerts/service-alert-event.model';
import { RequestRecordService } from '../requests-handling/request-round/request-record-service/request-record.service';
import { PaymentService } from '../backoffice/payments/payment-service/payment.service';
import {
  CaregiverType,
  EventType,
  PaymentStatus,
  RequestRecord,
} from '../shared/models/request-round/request-record.interface';
import { RequestPaymentBody } from '../shared/models/request-payment-body.model';
import { getURLRelated } from '../shared/utils.functions';
import { ToastService } from '../shared/services/toast-service/toast.service';
import { LongevityAlertsComponent } from './longevity-alerts/longevity-alerts.component';
import { LongevityOperatorTarget } from './longevity-alerts/longevity-alerts.model';

export interface AlertRequest {
  id: number;
  clientName: string;
  service: string;
  customerType: string;
  company: string;
  daysInStatus: number;
  operator: string;
}

export interface AlertSection {
  title: string;
  threshold: string;
  statusKey: string;
  daysBadgeClass: string;
  requests: AlertRequest[];
}

export type AlertsTab = 'selezione' | 'servizio' | 'longevity';

const STATUS_CONFIGS = [
  { status: 'NUOVA',             title: 'Nuove Richieste',    statusKey: 'nuova',                daysBadgeClass: 'days-neutral' },
  { status: 'SELEZIONE ATTIVA',  title: 'Selezione Attiva',   statusKey: 'selezione-attiva',     daysBadgeClass: 'days-alert'   },
  { status: 'INVIATO PREVENTIVO',title: 'Inviato Preventivo', statusKey: 'inviato-preventivo',   daysBadgeClass: 'days-alert'   },
  { status: 'DA RICHIAMARE',     title: 'Da Richiamare',      statusKey: 'da-richiamare',        daysBadgeClass: 'days-warning' },
] as const;

// Soglia "Eccezione": a switch spento si vedono le richieste in stato da al
// massimo MAX_DAYS giorni; a switch acceso si vedono solo quelle oltre
// MAX_DAYS (le uniche due viste sono complementari, senza sovrapposizioni).
const MAX_DAYS = 60;

@Component({
  selector: 'app-alerts-dashboard',
  templateUrl: './alerts-dashboard.component.html',
  styleUrls: ['./alerts-dashboard.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, LongevityAlertsComponent],
})
export class AlertsDashboardComponent implements OnInit, OnDestroy {
  isAdmin = false;
  selectedOperators: string[] = [];
  sections: AlertSection[] = [];
  isLoading = false;

  activeTab: AlertsTab = 'selezione';
  eccezioniOnly = false;

  pendingEvents: ServiceAlertEvent[] = [];
  toPayEvents: ServiceAlertEvent[] = [];
  isLoadingService = false;
  confirmingRecordId: string | null = null;
  /** FamilyTutor selezionati con il loro uid, per la tab Longevity. Nuovo array a ogni cambio di selezione. */
  longevityOperators: LongevityOperatorTarget[] = [];

  readonly operators = [
    { value: 'Valentina',     label: 'Valentina Riva',  uid: '6TNjE5wJmjfM57u7QEBwMHvjOWP2' },
    { value: 'Adele Missere', label: 'Adele Missere',   uid: 'ly69RYMMj3X22G42jWmrdo7S1Ls1' },
  ];

  private allRequestsByConfig: AlertRequest[][] = [];
  private unsubscribe = new Subject<void>();
  private cancelLoad = new Subject<void>();
  private cancelServiceLoad = new Subject<void>();

  constructor(
    private requestService: RequestService,
    private authService: AuthService,
    private userStorage: UserStorageService,
    private serviceAlertsService: ServiceAlertsService,
    private recordService: RequestRecordService,
    private paymentService: PaymentService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.authService
      .getToken()
      .pipe(
        filter((token) => token !== null),
        take(1),
        takeUntil(this.unsubscribe),
      )
      .subscribe((token) => {
        this.isAdmin = token!.admin ?? false;
        if (!this.isAdmin) {
          const operatorName = this.userStorage.getOperatorName();
          if (operatorName) {
            this.selectedOperators = [operatorName];
            this.syncLongevityOperators();
            this.loadAlerts();
          }
        }
      });
  }

  switchTab(tab: AlertsTab): void {
    this.activeTab = tab;
    if (tab === 'servizio') {
      this.loadServiceEvents();
    }
  }

  toggleEccezioni(): void {
    this.eccezioniOnly = !this.eccezioniOnly;
    this.applyEccezioniFilter();
  }

  isSelected(value: string): boolean {
    return this.selectedOperators.includes(value);
  }

  toggleOperator(value: string): void {
    const idx = this.selectedOperators.indexOf(value);
    if (idx >= 0) {
      this.selectedOperators = this.selectedOperators.filter((o) => o !== value);
    } else {
      this.selectedOperators = [...this.selectedOperators, value];
    }
    this.syncLongevityOperators();
    if (this.selectedOperators.length > 0) {
      this.loadAlerts();
      if (this.activeTab === 'servizio') {
        this.loadServiceEvents();
      }
    } else {
      this.sections = [];
      this.allRequestsByConfig = [];
      this.pendingEvents = [];
      this.toPayEvents = [];
    }
  }

  selectAll(): void {
    this.selectedOperators = this.operators.map((o) => o.value);
    this.syncLongevityOperators();
    this.loadAlerts();
    if (this.activeTab === 'servizio') {
      this.loadServiceEvents();
    }
  }

  deselectAll(): void {
    this.selectedOperators = [];
    this.syncLongevityOperators();
    this.sections = [];
    this.allRequestsByConfig = [];
    this.pendingEvents = [];
    this.toPayEvents = [];
  }

  get showOperatorColumn(): boolean {
    return this.selectedOperators.length > 1;
  }

  get hasAlerts(): boolean {
    return this.sections.length > 0;
  }

  get isContentReady(): boolean {
    return this.selectedOperators.length > 0 && !this.isLoading;
  }

  get hasServiceEvents(): boolean {
    return this.pendingEvents.length > 0 || this.toPayEvents.length > 0;
  }

  private loadAlerts(): void {
    this.cancelLoad.next(); // annulla eventuale chiamata precedente ancora in volo
    this.isLoading = true;
    this.sections = [];
    const now = new Date();

    type CallResult = { operatorName: string; configIdx: number; content: Request[] };

    // Build one call per (operator × status) using a plain loop to avoid flatMap
    const calls: Observable<CallResult>[] = [];
    for (const operatorName of this.selectedOperators) {
      for (let configIdx = 0; configIdx < STATUS_CONFIGS.length; configIdx++) {
        const cfg = STATUS_CONFIGS[configIdx];
        calls.push(
          this.requestService
            .getRequests({ status: cfg.status, operator: operatorName }, 0, 200, 'DESC')
            .pipe(map((page) => ({ operatorName, configIdx, content: page.content } as CallResult))),
        );
      }
    }

    forkJoin(calls)
      .pipe(takeUntil(this.cancelLoad), takeUntil(this.unsubscribe))
      .subscribe({
        next: (results: CallResult[]) => {
          // Accumulate requests per status bucket
          const buckets: AlertRequest[][] = STATUS_CONFIGS.map(() => []);

          for (const result of results) {
            const mapped = this.mapRequests(result.content, now, result.operatorName);
            for (const r of mapped) {
              buckets[result.configIdx].push(r);
            }
          }

          // Sort each bucket by daysInStatus descending
          for (const bucket of buckets) {
            bucket.sort((a, b) => b.daysInStatus - a.daysInStatus);
          }

          this.allRequestsByConfig = buckets;
          this.applyEccezioniFilter();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private applyEccezioniFilter(): void {
    this.sections = STATUS_CONFIGS
      .map((cfg, i) => ({
        title: cfg.title,
        threshold: this.eccezioniOnly ? `> ${MAX_DAYS} giorni` : `0-${MAX_DAYS} giorni`,
        statusKey: cfg.statusKey,
        daysBadgeClass: cfg.daysBadgeClass,
        requests: (this.allRequestsByConfig[i] ?? []).filter((r) =>
          this.eccezioniOnly ? r.daysInStatus > MAX_DAYS : r.daysInStatus <= MAX_DAYS,
        ),
      }))
      .filter((section) => section.requests.length > 0);
  }

  private mapRequests(
    requests: Request[],
    now: Date,
    operatorName: string,
  ): AlertRequest[] {
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    return requests.map((req) => {
      const refDate = new Date(req.operationalInfo.statusUpdated ?? req.timestamp);
      const daysInStatus = Math.floor(
        (now.getTime() - refDate.getTime()) / MS_PER_DAY,
      );
      return {
        id: req.id,
        clientName: `${req.requestPersonalInfo.parentName ?? ''} ${req.requestPersonalInfo.parentSurname ?? ''}`.trim(),
        service: req.requestPersonalInfo.requestCategory ?? '-',
        customerType: req.requestPersonalInfo.customer ?? '-',
        company: req.requestPersonalInfo.company || '-',
        daysInStatus,
        operator: operatorName,
      };
    });
  }

  private loadServiceEvents(): void {
    this.cancelServiceLoad.next(); // annulla eventuale chiamata precedente ancora in volo
    this.isLoadingService = true;

    type CallResult = { operatorName: string; records: ServiceRecordSearchResult[] };

    const calls: Observable<CallResult>[] = [];
    for (const operatorName of this.selectedOperators) {
      const uid = this.getOperatorUid(operatorName);
      if (!uid) {
        continue;
      }
      calls.push(
        this.serviceAlertsService
          .find([PaymentStatus.PENDING, PaymentStatus.TO_PAY], uid)
          .pipe(map((records) => ({ operatorName, records } as CallResult))),
      );
    }

    if (calls.length === 0) {
      this.pendingEvents = [];
      this.toPayEvents = [];
      this.isLoadingService = false;
      return;
    }

    forkJoin(calls)
      .pipe(takeUntil(this.cancelServiceLoad), takeUntil(this.unsubscribe))
      .subscribe({
        next: (results: CallResult[]) => {
          const pending: ServiceAlertEvent[] = [];
          const toPay: ServiceAlertEvent[] = [];
          for (const { operatorName, records } of results) {
            for (const record of records) {
              const mapped = this.toServiceAlertEvent(record, operatorName);
              if (record.event.paymentStatus === PaymentStatus.PENDING) {
                pending.push(mapped);
              } else if (record.event.paymentStatus === PaymentStatus.TO_PAY) {
                toPay.push(mapped);
              }
            }
          }
          this.pendingEvents = pending;
          this.toPayEvents = toPay;
          this.isLoadingService = false;
        },
        error: () => {
          this.isLoadingService = false;
        },
      });
  }

  private syncLongevityOperators(): void {
    this.longevityOperators = this.selectedOperators.map((name) => ({ name, uid: this.getOperatorUid(name) }));
  }

  /** Risolve lo uid Firebase dell'operatore: priorità al FamilyTutor loggato, altrimenti dalla lista admin. */
  private getOperatorUid(operatorName: string): string | null {
    const currentUser = this.userStorage.user;
    if (currentUser?.name === operatorName && currentUser.uid) {
      return currentUser.uid;
    }
    return this.operators.find((o) => o.value === operatorName)?.uid ?? null;
  }

  private toServiceAlertEvent(record: ServiceRecordSearchResult, operator: string): ServiceAlertEvent {
    return {
      recordId: record.event.id,
      requestId: record.event.requestId,
      clientName: record.request.clientName,
      service: record.request.businessLine,
      customerType: record.request.customerType,
      startDate: record.event.startDate,
      endDate: record.event.endDate,
      caregiverId: record.event.caregiverId,
      caregiverName: record.event.caregiverName,
      hoursWorked: record.event.hoursWorked,
      sessionAmount: record.event.sessionAmount,
      paymentLink: record.event.paymentLink,
      operator,
    };
  }

  copyPaymentLink(paymentLink: string | null): void {
    if (!paymentLink) {
      this.toast.setAction('Nessun link di pagamento disponibile per questo evento', false);
      return;
    }
    navigator.clipboard.writeText(paymentLink);
    this.toast.setAction('Link di pagamento copiato con successo!', true);
  }

  /**
   * Conferma un evento "Da validare": genera il link di pagamento, salva il
   * record in stato "Da pagare" e allinea lo stato della richiesta.
   */
  confirmEvent(event: ServiceAlertEvent): void {
    if (
      event.hoursWorked == null || event.hoursWorked < 0 ||
      event.sessionAmount == null || event.sessionAmount < 0
    ) {
      this.toast.setAction('Inserisci ore effettuate e importo seduta validi prima di confermare', false);
      return;
    }

    this.confirmingRecordId = event.recordId;

    this.requestService
      .getRequestByID(`${event.requestId}`)
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((request) => {
          const personalInfo = request.requestPersonalInfo;
          const paymentBody: RequestPaymentBody = {
            amount: event.sessionAmount,
            email: personalInfo.parentEmail,
            name: personalInfo.parentName,
            phone: personalInfo.parentPhone,
            provinceCode: personalInfo.provincia,
            seller: personalInfo.operator,
            service: personalInfo.serviceLinked?.id,
            surname: personalInfo.parentSurname,
            urlRelated: getURLRelated('REQUEST', `${request.id}`),
          };
          return this.paymentService.createPaymentLink(paymentBody).pipe(
            switchMap((paymentResponse) => {
              event.paymentLink = paymentResponse.paymentURL;
              const updatedRecord: RequestRecord = {
                id: event.recordId,
                requestId: event.requestId,
                startDate: event.startDate,
                endDate: event.endDate,
                eventType: EventType.USAGE,
                caregiverId: event.caregiverId ?? 'NESSUN CAREGIVER',
                caregiverName: event.caregiverName ?? 'NESSUN CAREGIVER',
                caregiverType: event.service as CaregiverType,
                hoursWorked: event.hoursWorked,
                paymentStatus: PaymentStatus.TO_PAY,
                paymentLink: paymentResponse.paymentURL,
                discounted: false,
                sessionAmount: event.sessionAmount,
                notes: null,
                createdAt: event.startDate,
                updatedAt: new Date().toISOString(),
              };
              return this.recordService.updateRecord(event.recordId, updatedRecord).pipe(
                switchMap(() =>
                  this.requestService.changeStatus(event.requestId, 'DA PAGARE'),
                ),
              );
            }),
          );
        }),
        catchError((err) => {
          this.toast.setAction("Errore durante la conferma dell'evento", false);
          this.confirmingRecordId = null;
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result === null) {
          return;
        }
        this.toast.setAction(
          'Evento confermato: link di pagamento generato e richiesta aggiornata',
          true,
        );
        this.pendingEvents = this.pendingEvents.filter((e) => e.recordId !== event.recordId);
        this.toPayEvents = [...this.toPayEvents, event];
        this.confirmingRecordId = null;
      });
  }

  ngOnDestroy(): void {
    this.cancelLoad.next();
    this.cancelLoad.complete();
    this.cancelServiceLoad.next();
    this.cancelServiceLoad.complete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
